<template>
  <div style="display:flex;flex-direction:column;gap:20px;">
    <!-- Donut: status -->
    <div class="panel">
      <div class="panel-head">
        <div class="panel-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21.21 15.89A10 10 0 1 1 8 2.83"/>
            <path d="M22 12A10 10 0 0 0 12 2v10z"/>
          </svg>
          Status das Conversas
        </div>
      </div>
      <div class="panel-body">
        <div class="donut-wrap">
          <canvas ref="donutEl" width="170" height="170"></canvas>
          <div class="dlegend">
            <div v-for="s in donutSegs" :key="s.label" class="dleg-row">
              <div class="dleg-l">
                <div class="dleg-dot" :style="{ background: s.color }"></div>
                {{ s.label }}
              </div>
              <span class="dleg-v">{{ s.value }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bar: por time -->
    <div class="panel">
      <div class="panel-head">
        <div class="panel-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <line x1="9" y1="9" x2="9" y2="15"/>
            <line x1="12" y1="7" x2="12" y2="15"/>
            <line x1="15" y1="11" x2="15" y2="15"/>
          </svg>
          Conversas por Time
        </div>
      </div>
      <div class="panel-body">
        <div class="bar-list">
          <div v-for="(item, i) in barItems" :key="item.name" class="bar-row">
            <div class="bar-lbl" :title="item.name">{{ item.name }}</div>
            <div class="bar-track">
              <div
                class="bar-fill"
                :ref="el => setBarRef(el as HTMLElement, i)"
                :data-pct="item.pct"
              ></div>
            </div>
            <div class="bar-n">{{ item.count }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Agents -->
    <div class="panel">
      <div class="panel-head">
        <div class="panel-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="8" r="4"/>
            <path d="M20 21a8 8 0 1 0-16 0"/>
          </svg>
          Agentes
        </div>
      </div>
      <div class="panel-body" style="padding:0 20px;">
        <div class="agent-list">
          <!-- Skeleton: ainda esperando o primeiro carregamento -->
          <template v-if="scopeReady === false">
            <div v-for="n in 3" :key="`skel-${n}`" class="agent-row">
              <div class="skel skel-av"></div>
              <div class="agent-info">
                <div class="skel skel-md" style="margin-bottom:5px"></div>
                <div class="skel skel-sm"></div>
              </div>
            </div>
          </template>
          <div v-else-if="!agentItems.length" class="empty" style="padding:20px"><p>Sem agentes</p></div>
          <template v-else>
            <div
              v-for="agent in pagedAgentItems"
              :key="agent.id"
              class="agent-row agent-row--clickable"
              @click="emit('openAgentSummary', agent, props.conversations.filter(c => c.assignee?.id === agent.id))"
            >
              <img
                v-if="agent.thumbnail"
                :src="agent.thumbnail"
                :alt="agent.name"
                class="av-img"
                @mouseenter="onAvatarEnter(agent, $event)"
                @mouseleave="$emit('hidePopup')"
                @error="(e: Event) => handleImgError(e, agent)"
              />
              <div
                v-else
                class="av"
                :style="{ background: avatarColor(agent.name) }"
                @mouseenter="onAvatarEnter(agent, $event)"
                @mouseleave="$emit('hidePopup')"
              >{{ initials(agent.name) }}</div>
              <div class="agent-info">
                <div class="agent-n">{{ agent.name }}</div>
                <div v-if="!agentTeamsMap || !Object.keys(agentTeamsMap).length" class="skel skel-sm"></div>
                <div v-else class="agent-e">{{ profileLabel(agent.id, agentTeamsMap?.[agent.id] ?? []) }}</div>
              </div>
              <div class="conv-count">{{ agent.count }}</div>
            </div>
          </template>
        </div>

        <!-- Paginação -->
        <div v-if="agentTotalPages > 1" class="agents-pagination agents-pagination--compact">
          <button
            class="ap-btn"
            :disabled="agentPage <= 1"
            @click="agentPage--"
            title="Página anterior"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>

          <span class="ap-info" style="margin-left:0">
            {{ (agentPage - 1) * AGENT_PAGE_SIZE + 1 }}–{{ Math.min(agentPage * AGENT_PAGE_SIZE, agentItems.length) }}
            de {{ agentItems.length }}
          </span>

          <button
            class="ap-btn"
            :disabled="agentPage >= agentTotalPages"
            @click="agentPage++"
            title="Próxima página"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import type { Conversation, PopupData } from '@/types/dashboard'
import { avatarColor, initials } from '@/composables/useHelpers'
import { profileLabel } from '@/composables/useAdmin'

const props = defineProps<{
  conversations: Conversation[]
  agentTeamsMap?: Record<number, number[]>
  // Fase 4 do plano de carregamento progressivo (docs/planos/PLANO-SKELETON-DASHBOARD.md):
  // sem isso, "Sem agentes" aparecia por engano enquanto conversations ainda
  // estava vazio (indistinguível de "carregou e realmente não tem agente").
  scopeReady?: boolean
}>()
const emit  = defineEmits<{
  showPopup:        [data: PopupData, el: HTMLElement]
  hidePopup:        []
  openAgentSummary: [agent: any, conversations: Conversation[]]
}>()

const donutEl = ref<HTMLCanvasElement | null>(null)

// Donut segments
const donutSegs = computed(() => {
  const c = props.conversations
  const open     = c.filter(x => x.status === 'open').length
  const resolved = c.filter(x => x.status === 'resolved').length
  const pending  = c.filter(x => x.status === 'pending').length
  const other    = c.length - open - resolved - pending
  return [
    { label: 'Aberta',    value: open,     color: '#0369A1' },
    { label: 'Resolvida', value: resolved, color: '#1F8C5A' },
    { label: 'Pendente',  value: pending,  color: '#FF6600' },
    { label: 'Outro',     value: other,    color: '#DDDCDC' },
  ].filter(s => s.value > 0)
})

// Bar items
const barItems = computed(() => {
  const map: Record<string, number> = {}
  props.conversations.forEach(c => {
    const k = c.team?.name || 'TypeBot'
    map[k] = (map[k] || 0) + 1
  })
  const sorted = Object.entries(map).sort((a, b) => b[1] - a[1])
  const max = sorted[0]?.[1] || 1
  return sorted.map(([name, count]) => ({ name, count, pct: count / max }))
})

// Agent items
const agentItems = computed(() => {
  const map: Record<number, any> = {}
  props.conversations.forEach(c => {
    if (!c.assignee) return
    const k = c.assignee.id
    if (!map[k]) map[k] = { ...c.assignee, count: 0, teams: new Set<string>() }
    map[k].count++
    if (c.team?.name) map[k].teams.add(c.team.name)
  })
  return Object.values(map).sort((a, b) => b.count - a.count)
})

// Paginação — mesmo PAGE_SIZE de AgentsTab.vue (aba "Agentes" com os cards),
// pra não sobrar espaço vazio numa lista enquanto a outra pagina diferente.
const AGENT_PAGE_SIZE = 12
const agentPage = ref(1)

const agentTotalPages = computed(() => Math.ceil(agentItems.value.length / AGENT_PAGE_SIZE))

const pagedAgentItems = computed(() => {
  const start = (agentPage.value - 1) * AGENT_PAGE_SIZE
  return agentItems.value.slice(start, start + AGENT_PAGE_SIZE)
})

watch(agentItems, () => { agentPage.value = 1 })

// Draw donut
watch(donutSegs, drawDonut, { immediate: false })
function drawDonut() {
  const canvas = donutEl.value
  if (!canvas) return
  const ctx = canvas.getContext('2d')!
  const W = canvas.width, H = canvas.height, cx = W / 2, cy = H / 2
  const R = W * 0.41, r = W * 0.25
  const segs  = donutSegs.value
  const total = segs.reduce((s, d) => s + d.value, 0)
  ctx.clearRect(0, 0, W, H)
  if (!total) {
    ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.arc(cx, cy, r, 0, Math.PI * 2, true)
    ctx.fillStyle = '#F0EFEF'; ctx.fill()
  } else {
    let angle = -Math.PI / 2
    segs.forEach(seg => {
      const sweep = (seg.value / total) * Math.PI * 2
      ctx.beginPath()
      ctx.moveTo(cx + r * Math.cos(angle), cy + r * Math.sin(angle))
      ctx.arc(cx, cy, R, angle, angle + sweep)
      ctx.arc(cx, cy, r, angle + sweep, angle, true)
      ctx.closePath()
      ctx.fillStyle = seg.color; ctx.fill()
      angle += sweep
    })
  }
  ctx.fillStyle = '#1A1A1A'; ctx.font = `800 ${W * .17}px Poppins,Arial`
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
  ctx.fillText(String(total), cx, cy - 4)
  ctx.fillStyle = '#6B6B6B'; ctx.font = `400 ${W * .08}px Poppins,Arial`
  ctx.fillText('total', cx, cy + H * .13)
}

// Animate bars
const barRefs: HTMLElement[] = []
function setBarRef(el: HTMLElement | null, i: number) {
  if (el) barRefs[i] = el
}

watch(barItems, async () => {
  await nextTick()
  barRefs.forEach((b, i) => {
    if (!b) return
    const pct = parseFloat(b.dataset.pct || '0')
    setTimeout(() => {
      b.style.transition = `transform .7s cubic-bezier(.4,0,.2,1) ${i * 60}ms`
      b.style.transform  = `scaleX(${pct})`
    }, 50)
  })
})

function handleImgError(e: Event, agent: any) {
  const img = e.target as HTMLImageElement
  const div = document.createElement('div')
  div.className = 'av'
  div.style.background = avatarColor(agent.name)
  div.style.cursor = 'pointer'
  div.textContent = initials(agent.name)
  div.addEventListener('mouseenter', ev => onAvatarEnter(agent, ev as MouseEvent))
  div.addEventListener('mouseleave', () => emit('hidePopup'))
  img.replaceWith(div)
}

function onAvatarEnter(agent: any, e: MouseEvent) {
  emit('showPopup', {
    name:      agent.name,
    role:      profileLabel(agent.id, props.agentTeamsMap?.[agent.id] ?? []),
    email:     agent.email || '',
    thumbnail: agent.thumbnail || '',
    status:    agent.availability_status || 'offline',
    convs:     agent.count,
    team:      [...agent.teams].join(', ') || null,
  }, e.currentTarget as HTMLElement)
}
</script>
